/**
 * RoutingPage: which station makes which item (FR-ORD-20, ADR 0027), read-only, with a *Test routing* form: pick an
 * item and a sale type and see the station it would go to and why (the same decision a send makes).
 *
 * Props: `mode`. At the store the page also lists every station's routing rules as HO set them up. At HO the rules
 * are edited on the Stations screens of the master data, so the page is only the test form, for the store picked
 * first (the API needs one).
 */
import { Button, ComboBox, InlineNotification, Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow, Tile } from '@carbon/react';
import { localized, useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { useReferenceOptions } from '../fields/references';
import { ChoiceDropdown } from '../filters/ResourceFilters';
import { fold, ordersKey, useOrderSetup, useOrdersApi } from '../orders/model';
import { asProblem, problemMessage } from '../problems';
import type { RoutingPreview, StationRouting } from './kitchenApi';
import { kitchenKey, useKitchenApi, type KitchenMode } from './model';
import { StorePicker } from './StorePicker';
import './kitchen.scss';

interface Option {
  value: string;
  label: string;
}

export function RoutingPage({ mode }: { mode: KitchenMode }) {
  const { t } = useTranslation();
  const api = useKitchenApi();
  const orders = useOrdersApi();
  const { setup, saleTypeName } = useOrderSetup();
  const [storeId, setStoreId] = useState('');
  const [saleTypeId, setSaleTypeId] = useState('');
  const [itemId, setItemId] = useState('');
  const hoItems = useReferenceOptions(mode === 'ho' ? 'catalog.item' : undefined);
  const hoSaleTypes = useReferenceOptions(mode === 'ho' ? 'config.sale_type' : undefined);

  const saleTypes: Option[] =
    mode === 'ho'
      ? (hoSaleTypes.data ?? [])
      : (setup?.saleTypes ?? []).map((s) => ({ value: s.id, label: saleTypeName(s.id, s.code) }));
  // The store's priced menu names the items it can sell; it is also how rules are shown by item name.
  const firstSaleType = saleTypeId || saleTypes[0]?.value || '';
  const menu = useQuery({
    queryKey: [...ordersKey, 'menu', firstSaleType],
    queryFn: () => orders.menu(firstSaleType),
    enabled: mode === 'store' && firstSaleType !== '',
    staleTime: 5 * 60_000,
  });
  const items: Option[] =
    mode === 'ho'
      ? (hoItems.data ?? [])
      : (menu.data?.items ?? []).map((i) => ({ value: i.id, label: `${i.code} · ${localized(i.nameI18n, i.name)}` }));

  const rules = useQuery({
    queryKey: [...kitchenKey, 'rules'],
    queryFn: () => api.routingRules(),
    enabled: mode === 'store',
  });

  const preview = useMutation({
    mutationFn: () => api.preview({ itemId, saleTypeId, storeId: mode === 'ho' ? storeId : undefined }),
  });
  const ready = itemId !== '' && saleTypeId !== '' && (mode === 'store' || storeId !== '');

  return (
    <section className="pos-admin-page">
      <PageHeader
        title={t('admin.kitchen.routing.title')}
        description={t(mode === 'ho' ? 'admin.kitchen.routing.hoHint' : 'admin.kitchen.routing.hint')}
      />

      <h2 className="pos-kitchen__section-title">{t('admin.kitchen.routing.test')}</h2>
      <form
        className="pos-kitchen__form"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) preview.mutate();
        }}
      >
        {mode === 'ho' && <StorePicker value={storeId} onChange={setStoreId} />}
        <ChoiceDropdown
          id="routing-sale-type"
          label={t('admin.kitchen.routing.saleType')}
          choices={saleTypes.map((s) => ({ id: s.value, label: s.label }))}
          value={saleTypeId}
          onChange={(v) => {
            setSaleTypeId(v);
            preview.reset();
          }}
        />
        <ComboBox<Option>
          id="routing-item"
          titleText={t('admin.kitchen.routing.item')}
          placeholder={t('admin.kitchen.routing.itemPlaceholder')}
          items={items}
          itemToString={(o) => o?.label ?? ''}
          shouldFilterItem={({ item, inputValue }) => !inputValue || fold(item.label).includes(fold(inputValue))}
          selectedItem={items.find((o) => o.value === itemId) ?? null}
          onChange={({ selectedItem }) => {
            setItemId(selectedItem?.value ?? '');
            preview.reset();
          }}
        />
        <Button type="submit" disabled={!ready || preview.isPending}>
          {t('admin.kitchen.routing.run')}
        </Button>
      </form>
      {preview.error && (
        <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(preview.error))} />
      )}
      {preview.data && <PreviewResult preview={preview.data} />}

      {mode === 'store' && (
        <>
          <h2 className="pos-kitchen__section-title">{t('admin.kitchen.routing.rules')}</h2>
          {rules.error ? (
            <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(rules.error))} />
          ) : (rules.data ?? []).length === 0 && !rules.isPending ? (
            <EmptyState headingLevel={3} heading={t('admin.kitchen.routing.noRules')} description={t('admin.kitchen.routing.noRulesHint')} />
          ) : (
            <RulesTable
              rules={rules.data ?? []}
              itemName={(id) => items.find((i) => i.value === id)?.label}
              saleTypeLabel={(id) => (id ? saleTypeName(id, id) : t('admin.kitchen.routing.allSaleTypes'))}
            />
          )}
        </>
      )}
    </section>
  );
}

function PreviewResult({ preview }: { preview: RoutingPreview }) {
  const { t } = useTranslation();
  const stations = preview.stations.length > 0 ? preview.stations : preview.defaultStation ? [preview.defaultStation] : [];
  return (
    <Tile>
      <dl className="pos-kitchen__facts">
        <div>
          <dt>{t('admin.kitchen.routing.station')}</dt>
          <dd>{stations.length > 0 ? stations.map((s) => `${s.code} · ${s.name}`).join(', ') : t('admin.kitchen.routing.nowhere')}</dd>
        </div>
        <div>
          <dt>{t('admin.kitchen.routing.reason')}</dt>
          <dd>
            {t(`admin.kitchen.routing.reasons.${preview.reason}` as I18nKey, { defaultValue: preview.reason })}
          </dd>
        </div>
      </dl>
    </Tile>
  );
}

function RulesTable({
  rules,
  itemName,
  saleTypeLabel,
}: {
  rules: StationRouting[];
  itemName(id: string): string | undefined;
  saleTypeLabel(id: string | null): string;
}) {
  const { t } = useTranslation();
  const sorted = [...rules].sort((a, b) => a.stationCode.localeCompare(b.stationCode) || Number(b.priority) - Number(a.priority));
  return (
    <TableContainer>
      <Table aria-label={t('admin.kitchen.routing.rules')}>
        <TableHead>
          <TableRow>
            <TableHeader>{t('admin.kitchen.routing.station')}</TableHeader>
            <TableHeader>{t('admin.kitchen.routing.appliesTo')}</TableHeader>
            <TableHeader>{t('admin.kitchen.routing.saleType')}</TableHeader>
            <TableHeader>{t('admin.kitchen.routing.priority')}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{`${r.stationCode} · ${r.stationName}`}</TableCell>
              <TableCell>
                {r.itemId
                  ? t('admin.kitchen.routing.ruleItem', { name: itemName(r.itemId) ?? r.itemId.slice(0, 8) })
                  : t('admin.kitchen.routing.ruleCategory', { name: r.itemCategoryId?.slice(0, 8) ?? '' })}
              </TableCell>
              <TableCell>{saleTypeLabel(r.saleTypeId)}</TableCell>
              <TableCell className="pos-admin-page__number">{r.priority}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
